import React, { useMemo, useState } from "react";
import useTemplate from "../../hooks/useTemplate";
import { useParams } from "react-router-dom";
import MuPb from "../../widgets/MuPb";
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { toastError } from "../../utils/util";
import TemplatePreview from "../../widgets/TemplatePreview";
import BackBtn from "../../widgets/BackBtn";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import InfoIcon from "@mui/icons-material/Info";
const TemplateView = () => {
  const { id } = useParams();
  const [sending, setSending] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const open = Boolean(anchorEl);
  const { data, isLoading, isError, error } = useTemplate(id);
  const headers = useMemo(
    () => (excelData.length > 0 ? Object.keys(excelData[0]) : []),
    [excelData]
  );

  // Handle loading and error states
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box m={2}>
        <Alert severity="error">
          {error?.message || "Failed to load template"}
        </Alert>
      </Box>
    );
  }

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setExcelLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const binaryStr = e.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        });

        if (jsonData.length === 0) {
          toast.warn("The uploaded file is empty");
          setExcelData([]);
          return;
        }

        // Validate required header
        const headers = jsonData[0];
        if (!headers.includes("WhatsApp_Number")) {
          toast.error('File must contain "WhatsApp_Number" column');
          setExcelData([]);
          return;
        }

        // Format data
        const formattedData = jsonData.slice(1).map((row) => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] ?? "";
          });
          return obj;
        });

        setExcelData(formattedData);
      } catch (err) {
        toast.error("Error reading file");
        console.error("File read error:", err);
      } finally {
        setExcelLoading(false);
      }
    };

    reader.onerror = () => {
      toast.error("Failed to read file");
      setExcelLoading(false);
    };

    reader.readAsBinaryString(file);
  };

  const handleSend = async () => {
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }

    if (excelData.length === 0) {
      toast.error("Please upload contact data");
      return;
    }

    try {
      setSending(true);
      const response = await axios.post(`${API_ENDPOINT}/api/test-campaign`, {
        id,
        cusId: "MEDIMINDS EDUSERVES LLP", // Consider making this dynamic
        campaignName,
        contacts: excelData,
      });
      toast.success(response.data.message);
      setExcelData([]); // Clear data after successful send
      setCampaignName("");
    } catch (error) {
      toastError(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackBtn />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="h6" fontWeight="500" gutterBottom>
              Import Contacts
            </Typography>

            <Stack direction="row" alignItems="center" mb={2}>
              <Typography variant="body2">
                Please read these instructions before importing
              </Typography>
              <IconButton
                onClick={handlePopoverOpen}
                size="small"
                sx={{ ml: 1 }}
              >
                <InfoIcon />
              </IconButton>
            </Stack>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: 3,
                  border: "1px solid",
                  borderColor: "grey.200",
                },
              }}
            >
              <Box sx={{ p: 2, maxWidth: 320 }}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  fontWeight="600"
                  mb={1}
                >
                  File Requirements
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0, "& li": { mb: 1 } }}>
                  <Typography component="li" variant="body2">
                    The first column must be the whatsapp numbers
                  </Typography>
                  <Typography component="li" variant="body2">
                    After first column, your variable names(numbered eg.{" "}
                    {`{{1}}`} or named eg. {`{{'order_id'}}`}) must appear in
                    the same order in which they appear in the message body.
                  </Typography>
                  <Typography component="li" variant="body2">
                    Atleast one line item should be there
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, textAlign: "right" }}>
                  <Button size="small" onClick={handlePopoverClose}>
                    Got it
                  </Button>
                </Box>
              </Box>
            </Popover>

            <Button
              startIcon={<ImportExportIcon />}
              variant="contained"
              component="label"
              disabled={excelLoading}
              sx={{ mb: 3 }}
            >
              {excelLoading ? "Processing..." : "Import Excel File"}
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                hidden
              />
            </Button>
            {excelData.length > 10 && (
              <Typography variant="caption">
                First 10 items in the sheet
              </Typography>
            )}
            {excelData.length > 0 && (
              <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => (
                        <TableCell
                          key={header}
                          sx={{ bgcolor: "grey.100", fontWeight: "bold" }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {excelData.slice(0, 10).map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {headers.map((header) => (
                          <TableCell key={`${rowIndex}-${header}`}>
                            {row[header]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <TextField
              fullWidth
              label="Campaign Name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
            />

            <LoadingButton
              loading={sending}
              variant="contained"
              onClick={handleSend}
              disabled={excelLoading || !campaignName.trim()}
              sx={{ mt: 2 }}
            >
              Test Campaign
            </LoadingButton>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TemplatePreview template={data} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default TemplateView;
